import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { Loader2, ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const brandInitial = { opacity: 0, y: 12 };
const brandAnimate = { opacity: 1, y: 0 };

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", password: "", confirm: "" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }
    setSubmitting(true);
    const res = await register({
      company_name: form.company_name.trim(),
      contact_name: form.contact_name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      password: form.password,
    });
    setSubmitting(false);
    if (res.ok) {
      toast.success("Account created — welcome!");
      navigate("/welcome");
    } else {
      setError(res.error);
      toast.error(res.error || "Could not create account");
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block bg-[#0F172A] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&q=80&w=1600"
          alt="Aviation"
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-[#0F172A]/70 noise-overlay" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-11 w-auto bg-white rounded-md px-3 py-2" />
            <span className="text-white font-display font-bold tracking-tight">PEREGRINE COMPANIES PORTAL</span>
          </div>
          <div>
            <h1 className="text-white font-display font-extrabold text-5xl leading-[0.95] tracking-tight">
              Create<br />your<br /><span className="text-[#D4AF37]">account.</span>
            </h1>
            <p className="mt-6 text-zinc-300 max-w-sm text-base leading-relaxed">
              Register to submit your trade credit references and get set up for a line of credit with Peregrine Companies.
            </p>
          </div>
          <div className="text-xs text-zinc-400 font-mono">
            6318 Union Avenue, Shreveport, LA 71106 · 318.222.2224
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <motion.div initial={brandInitial} animate={brandAnimate} transition={{ duration: 0.4 }} className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <img src="/paslogo.png" alt="Peregrine Companies" className="h-9 w-auto" />
            <span className="font-display font-bold tracking-tight text-[#0F172A]">PEREGRINE COMPANIES PORTAL</span>
          </div>

          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">New Customers</p>
          <h2 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Create a Login</h2>
          <p className="mt-2 text-zinc-500">Set up your account to begin your credit reference application.</p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="register-form">
            <Field label="Company name" value={form.company_name} onChange={set("company_name")} testid="reg-company" required />
            <Field label="Contact name" value={form.contact_name} onChange={set("contact_name")} testid="reg-contact" required />
            <Field label="Email address" type="email" value={form.email} onChange={set("email")} testid="reg-email" mono required placeholder="you@company.com" />
            <Field label="Phone number" value={form.phone} onChange={set("phone")} testid="reg-phone" />
            <Field label="Password" type="password" value={form.password} onChange={set("password")} testid="reg-password" mono required placeholder="At least 8 characters" />
            <Field label="Confirm password" type="password" value={form.confirm} onChange={set("confirm")} testid="reg-confirm" mono required />

            {error && (
              <p className="text-sm text-[#EF4444]" data-testid="register-error">{error}</p>
            )}

            <button
              type="submit"
              disabled={submitting}
              data-testid="register-submit"
              className="group w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></>}
            </button>
          </form>

          <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-[#0F172A] transition-colors">
            <ArrowLeft className="h-4 w-4" /> Already have a login? Sign in
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid, mono, required, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-2">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        data-testid={testid}
        placeholder={placeholder}
        className={`w-full px-4 py-3 border border-zinc-300 bg-white text-[#0A0A0A] text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow ${mono ? "font-mono" : ""}`}
      />
    </div>
  );
}
